import React from "react";
import PropTypes from "prop-types";
import { Whisper, Tooltip } from "rsuite";
import cx from "classnames";
import Table from "../common/table/Table";
import PlusIcon from "../../assets/img/plus-circle.png";
import MinusIcon from "../../assets/img/minus-circle.png";
import SortUpDownIcon from "../../assets/img/up-down.png";
import SortAscIcon from "../../assets/img/sort-asc.svg";
import SortDescIcon from "../../assets/img/sort-desc.svg";
import PriceCheckDrilledDownView from "./PriceCheckDrilledDownView";
import InfoIcon from "../../assets/img/info.svg";
import { formatDateToMMMDDYYYY } from "../../pages/price-check/utils";
import {
  getMeshedLabel,
  getPromotions,
  getRatePlanCodes,
  getRatePlanCodesSummary,
} from "./PriceCheckTableUtils";
import "../common/SummaryTooltipCell.scss";

const { Column, HeaderCell, Cell } = Table;

// Helper function to calculate row expanded height
const calculateRowExpandedHeight = (rowData) => {
  const startDate = new Date(rowData.arrivalDate);
  const endDate = new Date(rowData.departureDate);
  const numberOfDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

  const payAtBookingTaxRows = rowData.payAtBookingTaxes?.length || 0;
  const payAtPropertyTaxRows = rowData.payAtPropertyTaxes?.length || 0;

  // Determine pay at property rows - always include this section
  const payAtPropertyRows = 1; // Always show pay at property row
  const payAtBookingRows = 1;

  // Calculate total breakdown rows including all sections
  const breakdownRows =
    1 + // Sub-totals row
    payAtBookingTaxRows + // Pay at booking tax rows
    payAtPropertyTaxRows + // Pay at property tax rows
    1 + // Total row
    payAtBookingRows + // Pay at booking row
    payAtPropertyRows; // Pay at property row

  // Dynamic height calculation based on content
  const baseHeight = 100; // Increased base height
  const titleHeight = 70; // Increased title height
  const tableHeaderHeight = 45; // Increased header height
  const dailyRowHeight = 45; // Increased daily row height for better readability
  const breakdownRowHeight = 40; // Increased breakdown row height
  const bottomPadding = 20; // Significantly increased padding to prevent cutoff
  const extraPaddingForLongRanges = Math.min(numberOfDays * 2, 1); // Increased extra padding for longer date ranges

  const totalHeight =
    baseHeight +
    titleHeight +
    tableHeaderHeight +
    numberOfDays * dailyRowHeight +
    breakdownRows * breakdownRowHeight +
    bottomPadding +
    extraPaddingForLongRanges;

  // Dynamic minimum height based on number of days - increased to ensure pay at property is visible
  const minHeight = Math.max(500, 100 + numberOfDays * 15);

  return Math.max(minHeight, totalHeight);
};

// Helper function to parse text into lines
const parseTextToLines = (text) => {
  if (!text || typeof text !== "string") return [];

  const lines = text.split(" /n ");
  return lines.map((line) => line.trim()).filter((line) => line);
};

// Helper function to render plain text with a hover tooltip
const renderTextWithTooltip = (text) => {
  if (!text) return "";

  return (
    <div className="tooltip-cell-wrapper">
      <Whisper
        placement="autoVertical"
        trigger="hover"
        preventOverflow
        speaker={<Tooltip className="long-text-tooltip">{text}</Tooltip>}
      >
        <span className="tooltip-cell-content">{text}</span>
      </Whisper>
    </div>
  );
};

// Helper function to render sort icon
const renderSortIcon = (dataKey, sortState, handleSort) => {
  const isCurrentSortColumn = sortState?.sortBy === dataKey;

  // Determine sort icon source
  let sortIconSrc;
  if (isCurrentSortColumn) {
    sortIconSrc =
      sortState?.sortDirection === "asc" ? SortAscIcon : SortDescIcon;
  } else {
    sortIconSrc = SortUpDownIcon;
  }

  // Determine sort icon alt text
  let sortIconAlt;
  if (isCurrentSortColumn) {
    sortIconAlt =
      sortState?.sortDirection === "asc"
        ? "Sorted ascending"
        : "Sorted descending";
  } else {
    sortIconAlt = "Sort";
  }

  const sortIconClass = `sort-icon ${isCurrentSortColumn ? "active" : ""}`;

  return (
    <button
      type="button"
      className={sortIconClass}
      onClick={() => handleSort?.(dataKey)}
      aria-label={sortIconAlt}
    >
      <img src={sortIconSrc} alt="" />
    </button>
  );
};

// Helper function to render date cell
const renderDateCell = (dataKey) => (
  <Cell>{(rowData) => formatDateToMMMDDYYYY(rowData[dataKey])}</Cell>
);

// Helper function to render gross price cell
const renderGrossPriceCell = (handleToggleExpand) => (
  <Cell>
    {(rowData) => (
      <button
        type="button"
        className="total-price-link"
        onClick={(e) => {
          e.preventDefault();
          handleToggleExpand(rowData._uniqueId);
        }}
      >
        {rowData.grossPrice}
      </button>
    )}
  </Cell>
);

// Helper function to render promotion cell
const renderPromotionCell = () => (
  <Cell>
    {(rowData) => {
      const promotions = getPromotions(rowData);
      return (
        <span className="promotion-cell">
          <span className="promotion-text">
            {promotions.map((promotion) => promotion.name).join(", ")}
          </span>
          {promotions.length > 0 && (
            <Whisper
              placement="auto"
              trigger="hover"
              preventOverflow
              speaker={
                <Tooltip className={cx("summary-tooltip", "long-text-tooltip")}>
                  {promotions.map((promotion, promotionIndex) => (
                    <div key={`promotion-${promotion.name}-${promotionIndex}`}>
                      {promotion.name}
                      <ul className="summary-tooltip-list">
                        {parseTextToLines(promotion.text).map(
                          (detail, index) => (
                            <li key={`promotion-${detail}-${index}`}>
                              {detail}
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  ))}
                </Tooltip>
              }
            >
              <img src={InfoIcon} alt="info" className="promotion-info-icon" />
            </Whisper>
          )}
        </span>
      );
    }}
  </Cell>
);

const renderCodesTooltip = (codes) => (
  <Tooltip className="summary-tooltip">
    <ul className="summary-tooltip-list">
      {codes.map((code) => (
        <li key={code}>{code}</li>
      ))}
    </ul>
  </Tooltip>
);

// Meshed rows list every rate plan code; other rows keep the display text
const renderRatePlanCell = () => (
  <Cell>
    {(rowData) => {
      const codes = rowData.meshed ? getRatePlanCodes(rowData) : [];
      if (codes.length === 0) {
        return renderTextWithTooltip(rowData.ratePlanDisplayText);
      }

      const { text, isTruncated, hiddenCount } = getRatePlanCodesSummary(codes);
      const textNode = (
        <span className="summary-tooltip-cell__primary">{text}</span>
      );
      return (
        <div className="summary-tooltip-cell">
          {isTruncated && hiddenCount === 0 ? (
            <Whisper
              placement="top"
              trigger="hover"
              speaker={renderCodesTooltip(codes)}
            >
              {textNode}
            </Whisper>
          ) : (
            textNode
          )}
          {hiddenCount > 0 && (
            <Whisper
              placement="top"
              trigger="hover"
              speaker={renderCodesTooltip(codes)}
            >
              <span className="summary-tooltip-cell__count">
                +{hiddenCount}
              </span>
            </Whisper>
          )}
        </div>
      );
    }}
  </Cell>
);

const renderMeshedCell = () => (
  <Cell>{(rowData) => getMeshedLabel(rowData)}</Cell>
);

// Helper function to render refundable cell
const renderRefundableCell = () => (
  <Cell>
    {(rowData) => {
      const refundableDetails = parseTextToLines(rowData.refundableText);
      const refundableStatus = rowData.refundable
        ? "Refundable"
        : "Non-refundable";
      return (
        <span className="refundable-cell">
          <span className="refundable-text">{refundableStatus}</span>
          {rowData.refundable && (
            <Whisper
              placement="auto"
              trigger="hover"
              preventOverflow
              speaker={
                <Tooltip className={cx("summary-tooltip", "long-text-tooltip")}>
                  <ul className="summary-tooltip-list">
                    {refundableDetails.map((detail, index) => (
                      <li key={`refundable-${detail}-${index}`}>{detail}</li>
                    ))}
                  </ul>
                </Tooltip>
              }
            >
              <img src={InfoIcon} alt="info" className="promotion-info-icon" />
            </Whisper>
          )}
        </span>
      );
    }}
  </Cell>
);

// Helper function to render tax cell
const renderTaxCell = () => (
  <Cell>
    {(rowData) => {
      const taxValue = rowData.tax;
      if (taxValue === undefined || taxValue === null) return "";

      // Show "Included" if tax value is 0, otherwise show the value
      if (parseFloat(taxValue) === 0) {
        return "Included";
      }

      return taxValue;
    }}
  </Cell>
);

// Helper function to render cell based on dataKey
const renderCell = (dataKey, handleToggleExpand) => {
  if (dataKey === "arrivalDate" || dataKey === "departureDate") {
    return renderDateCell(dataKey);
  }
  if (dataKey === "bookDate") {
    return renderDateCell(dataKey);
  }
  if (dataKey === "grossPrice") {
    return renderGrossPriceCell(handleToggleExpand);
  }
  if (dataKey === "ratePlanDisplayText") {
    return renderRatePlanCell();
  }
  if (dataKey === "meshed") {
    return renderMeshedCell();
  }
  if (dataKey === "promotionName") {
    return renderPromotionCell();
  }
  if (dataKey === "refundable") {
    return renderRefundableCell();
  }
  if (dataKey === "tax") {
    return renderTaxCell();
  }
  return <Cell>{(rowData) => renderTextWithTooltip(rowData[dataKey])}</Cell>;
};

const PriceCheckTable = ({
  priceCheckData,
  columns,
  isLoading,
  priceCheckPage,
  onPageChange,
  expandedRowKeys,
  handleToggleExpand,
  handleSort,
  sortState,
  formValues,
}) => {
  const dataWithIds = priceCheckData.map((row, index) => ({
    ...row,
    _uniqueId: row._uniqueId || `row-${index}-${row.propertyName || "unknown"}`,
  }));

  return (
    <Table
      data={dataWithIds}
      loading={isLoading}
      paginated
      onPageChange={onPageChange}
      pageSizeOptions={[10, 20, 30]}
      total={priceCheckPage.totalElements}
      defaultPageSize={priceCheckPage.size}
      currentPage={priceCheckPage.number + 1}
      rowKey="_uniqueId"
      rowExpandedHeight={calculateRowExpandedHeight}
      expandedRowKeys={expandedRowKeys}
      shouldUpdateScroll={false}
      renderRowExpanded={(rowData) => (
        <PriceCheckDrilledDownView rowData={rowData} formValues={formValues} />
      )}
    >
      <Column width={40} align="center" fixed>
        <HeaderCell />
        <Cell>
          {(rowData) => {
            const isExpanded = expandedRowKeys.includes(rowData._uniqueId);
            return (
              <button
                type="button"
                className="expand-button"
                onClick={() => handleToggleExpand(rowData._uniqueId)}
                aria-label={isExpanded ? "Collapse row" : "Expand row"}
              >
                <img
                  src={isExpanded ? MinusIcon : PlusIcon}
                  alt=""
                  className="expand-icon"
                />
              </button>
            );
          }}
        </Cell>
      </Column>
      {columns.map((column) => {
        const { dataKey, header, width } = column;
        const sortableKeys = ["arrivalDate", "departureDate", "grossPrice"];
        const isSortable = sortableKeys.includes(dataKey);
        return (
          <Column
            key={dataKey}
            width={width}
            {...(isSortable ? { sortable: true } : {})}
          >
            <HeaderCell>
              <span className="header-cell-content">
                {header}
                {isSortable && renderSortIcon(dataKey, sortState, handleSort)}
              </span>
            </HeaderCell>
            {renderCell(dataKey, handleToggleExpand)}
          </Column>
        );
      })}
    </Table>
  );
};

PriceCheckTable.propTypes = {
  priceCheckData: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  priceCheckPage: PropTypes.object.isRequired,
  onPageChange: PropTypes.func.isRequired,
  expandedRowKeys: PropTypes.array.isRequired,
  handleToggleExpand: PropTypes.func.isRequired,
  handleSort: PropTypes.func,
  sortState: PropTypes.object,
  formValues: PropTypes.shape({
    adults: PropTypes.number,
    children: PropTypes.number,
  }),
};

export default PriceCheckTable;
